import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Sparkles, Users, Star, Zap, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from '../common/Button';
import { getOfferings, Offering } from '../../services/offeringsService';

const HeroSection: React.FC = () => {
  const [offerings, setOfferings] = useState<Offering[]>([]);
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        const cfg = await getOfferings();
        if (mounted) setOfferings(cfg.offerings || []);
      } catch {
        if (mounted) setOfferings([]);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, []);

  const aiPrice = useMemo(() => {
    const byCode = new Map(offerings.map((o) => [o.code, o]));
    return byCode.get('AI_TRAVEL_STYLING')?.amountInr ?? 999;
  }, [offerings]);

  return (
    <section className="relative bg-off-white py-20 lg:py-32 overflow-hidden">
      {/* Animated gradient blobs */}
      <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] bg-coral/[0.07] rounded-full blur-[100px] animate-blob" />
      <div className="absolute bottom-[-10%] right-[-5%] w-[600px] h-[600px] bg-teal/[0.06] rounded-full blur-[120px] animate-blob-delay-2" />
      <div className="absolute top-[40%] right-[20%] w-[300px] h-[300px] bg-golden/[0.05] rounded-full blur-[80px] animate-blob-delay-4" />

      {/* Subtle grid overlay */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `linear-gradient(rgba(42,42,42,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(42,42,42,0.3) 1px, transparent 1px)`,
        backgroundSize: '80px 80px'
      }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center px-4 py-2 glass rounded-full text-sm font-medium mb-6 animate-fade-in shadow-glass">
              <div className="w-2 h-2 rounded-full bg-success-green mr-2 animate-smooth-pulse" />
              <span className="text-charcoal">AI + Expert Stylists</span>
              <span className="mx-2 text-gray-300">|</span>
              <span className="text-coral font-semibold">Travel, Events & Personal</span>
            </div>

            <h1 className="font-nunito font-bold text-3xl sm:text-4xl md:text-5xl lg:text-[3.5rem] xl:text-6xl text-charcoal leading-[1.1] mb-6 animate-fade-in-up animate-stagger-1">
              Expert Styling for{' '}
              <span className="text-gradient">Every Occasion</span>
            </h1>

            <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed animate-fade-in-up animate-stagger-2">
              From holidays to weddings, work trips to everyday looks. Get personalised styling from expert stylists, enhanced by cutting-edge AI.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10 animate-fade-in-up animate-stagger-3">
              <Link to="/app" className="group inline-flex items-center justify-center bg-coral hover:bg-coral/90 text-white rounded-2xl text-base sm:text-lg px-6 sm:px-8 py-3.5 sm:py-4 font-semibold transition-all duration-300 hover:shadow-glow-coral hover:scale-[1.02] w-full sm:w-auto">
                Start Your Style Journey
                <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>

              <Link to="/ai-analysis" className="w-full sm:w-auto">
                <Button variant="outline" size="lg" className="text-base sm:text-lg w-full rounded-2xl hover:shadow-glass transition-all duration-300">
                  Try AI Analysis
                </Button>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 sm:gap-5 animate-fade-in-up animate-stagger-4">
              <div className="flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-coral" />
                <span className="text-sm text-gray-600 font-medium">AI + Expert Stylists</span>
              </div>
              <div className="hidden sm:block w-px h-4 bg-gray-300" />
              <div className="flex items-center gap-1.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={12} className="text-golden fill-golden" />
                ))}
                <span className="text-sm text-gray-600 font-medium ml-0.5">4.9 rating</span>
              </div>
              <div className="hidden sm:block w-px h-4 bg-gray-300" />
              <div className="flex items-center gap-1.5">
                <Shield className="w-4 h-4 text-teal" />
                <span className="text-sm text-gray-600 font-medium">Secure payments</span>
              </div>
            </div>
          </div>

          {/* Hero Visual */}
          <div className="relative animate-fade-in-right animate-stagger-2 mt-8 lg:mt-0">
            {/* Glow behind card */}
            <div className="absolute inset-4 bg-gradient-to-br from-coral/20 to-teal/20 rounded-3xl blur-2xl" />

            <div className="relative z-10 gradient-border">
              <div className="bg-white rounded-[1.4rem] p-5 sm:p-6">
                {videoFailed ? (
                  <div className="w-full h-48 sm:h-56 lg:h-72 rounded-2xl mb-4 bg-gradient-to-br from-coral/10 via-white to-teal/10 border border-gray-100 flex items-center justify-center">
                    <div className="flex items-center gap-2 text-gray-600 text-sm font-medium">
                      <Sparkles className="w-4 h-4 text-teal" />
                      Preview unavailable
                    </div>
                  </div>
                ) : (
                  <video
                    src={`${process.env.PUBLIC_URL}/suitcase_hero_cover.mp4`}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-48 sm:h-56 lg:h-72 object-cover rounded-2xl mb-4"
                    onError={() => setVideoFailed(true)}
                  />
                )}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { icon: Sparkles, label: 'AI-powered outfits', color: 'coral' },
                    { icon: Users, label: 'Expert curated', color: 'teal' },
                    { icon: Zap, label: 'Instant delivery', color: 'golden' },
                  ].map((item) => (
                    <div key={item.label} className="flex flex-col items-center text-center p-2.5 rounded-xl bg-light-gray/60">
                      <item.icon size={16} className={`text-${item.color} mb-1`} />
                      <span className="text-xs text-gray-600 leading-tight">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Floating Elements - glassmorphism */}
            <div className="absolute -top-4 sm:-top-6 -right-2 sm:-right-6 glass shadow-glass-lg p-3 sm:p-4 rounded-2xl animate-fade-in animate-stagger-4 z-20 animate-float">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-coral/10 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-coral" />
                </div>
                <div>
                  <span className="font-bold text-sm sm:text-base text-charcoal">From ₹{aiPrice.toLocaleString('en-IN')}</span>
                  <p className="text-xs text-gray-500">AI Styling</p>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-4 sm:-bottom-6 -left-2 sm:-left-6 glass shadow-glass-lg p-3 sm:p-4 rounded-2xl animate-fade-in animate-stagger-5 z-20 animate-float" style={{ animationDelay: '1s' }}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-teal/10 flex items-center justify-center">
                  <Star className="w-4 h-4 text-golden fill-golden" />
                </div>
                <div>
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={10} className="text-golden fill-golden" />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">Top-rated service</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
